import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, setAuthToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SignOut, ChartBar, Users, Megaphone, EnvelopeSimple } from "@phosphor-icons/react";

function LoginScreen({ onLogin }) {
    const [form, setForm] = useState({ username: "", password: "" });
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/login", form);
            setAuthToken(data.token);
            toast.success(`Welcome, ${data.username}`);
            onLogin();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invalid credentials");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0A192F] flex items-center justify-center px-6 bg-grain relative" data-testid="admin-login-screen">
            <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-3xl" />
            <div className="relative w-full max-w-md">
                <div className="text-center mb-8">
                    <img src="/logo.png" alt="CashFlow Ads" className="mx-auto w-20 h-20 rounded-full object-cover bg-white shadow-2xl ring-4 ring-[#D4AF37]/40" />
                    <h1 className="font-display text-3xl font-bold text-white mt-4">Admin Portal</h1>
                    <p className="text-white/60 text-sm mt-1">CashFlow Ads — Internal Dashboard</p>
                </div>
                <form onSubmit={submit} className="bg-white rounded-2xl p-8 shadow-2xl space-y-4" data-testid="admin-login-form">
                    <div>
                        <Label htmlFor="u" className="text-[#0A192F] font-semibold">Username</Label>
                        <Input id="u" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1.5" data-testid="admin-input-username" />
                    </div>
                    <div>
                        <Label htmlFor="p" className="text-[#0A192F] font-semibold">Password</Label>
                        <Input id="p" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="admin-input-password" />
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full bg-[#0A192F] hover:bg-[#112240] text-white font-semibold py-6 rounded-full" data-testid="admin-login-button">
                        {submitting ? "Signing in..." : "Sign In"}
                    </Button>
                </form>
            </div>
        </div>
    );
}

function fmtDate(s) {
    if (!s) return "—";
    try {
        return new Date(s).toLocaleString();
    } catch {
        return s;
    }
}

export default function Admin() {
    const [authed, setAuthed] = useState(!!localStorage.getItem("cf_admin_token"));
    const [stats, setStats] = useState(null);
    const [advertisers, setAdvertisers] = useState([]);
    const [posters, setPosters] = useState([]);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [s, a, p, m] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/leads/advertiser"),
                api.get("/admin/leads/poster"),
                api.get("/admin/contact"),
            ]);
            setStats(s.data);
            setAdvertisers(a.data);
            setPosters(p.data);
            setMessages(m.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                setAuthToken(null);
                setAuthed(false);
                toast.error("Session expired. Please log in again.");
            } else {
                toast.error("Failed to load data");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authed) loadAll();
    }, [authed]);

    const logout = () => {
        setAuthToken(null);
        setAuthed(false);
    };

    if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

    return (
        <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
            <header className="bg-[#0A192F] text-white">
                <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/logo.png" alt="CashFlow Ads" className="w-10 h-10 rounded-full object-cover bg-white ring-2 ring-[#D4AF37]/40" />
                        <div>
                            <div className="font-display font-bold">CashFlow Ads · Admin</div>
                            <div className="text-[10px] text-[#D4AF37] uppercase tracking-wider">Internal Dashboard</div>
                        </div>
                    </div>
                    <Button variant="ghost" onClick={logout} className="text-white/85 hover:bg-white/10" data-testid="admin-logout-button">
                        <SignOut size={18} className="mr-2" /> Logout
                    </Button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 md:px-8 py-8">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {[
                        { i: Megaphone, l: "Advertiser Leads", v: stats?.advertiser_leads ?? "—", c: "#D4AF37" },
                        { i: Users, l: "Poster Applications", v: stats?.poster_applications ?? "—", c: "#0A192F" },
                        { i: EnvelopeSimple, l: "Contact Messages", v: stats?.contact_messages ?? "—", c: "#25D366" },
                        { i: ChartBar, l: "Total Submissions", v: (stats?.advertiser_leads ?? 0) + (stats?.poster_applications ?? 0) + (stats?.contact_messages ?? 0), c: "#0A192F" },
                    ].map((s) => (
                        <Card key={s.l} className="border-slate-100" data-testid={`admin-stat-${s.l.toLowerCase().replace(/\s+/g, "-")}`}>
                            <CardContent className="pt-6">
                                <s.i size={26} weight="duotone" color={s.c} />
                                <div className="font-display text-3xl font-bold text-[#0A192F] mt-2">{s.v}</div>
                                <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{s.l}</div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                <Tabs defaultValue="advertisers" className="w-full">
                    <TabsList className="bg-white border border-slate-200">
                        <TabsTrigger value="advertisers" data-testid="admin-tab-advertisers">Advertiser Leads ({advertisers.length})</TabsTrigger>
                        <TabsTrigger value="poster-apps" data-testid="admin-tab-posters">Poster Applications ({posters.length})</TabsTrigger>
                        <TabsTrigger value="messages" data-testid="admin-tab-messages">Contact Messages ({messages.length})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="advertisers" className="mt-4">
                        <Card>
                            <CardHeader><CardTitle className="font-display">Advertiser Leads</CardTitle></CardHeader>
                            <CardContent>
                                {advertisers.length === 0 ? (
                                    <div className="text-sm text-slate-500 py-8 text-center">{loading ? "Loading..." : "No leads yet."}</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Date</TableHead>
                                                    <TableHead>Business</TableHead>
                                                    <TableHead>Contact</TableHead>
                                                    <TableHead>Email</TableHead>
                                                    <TableHead>Phone</TableHead>
                                                    <TableHead>Industry</TableHead>
                                                    <TableHead>Location</TableHead>
                                                    <TableHead>Budget</TableHead>
                                                    <TableHead>Notes</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {advertisers.map((l) => (
                                                    <TableRow key={l.id}>
                                                        <TableCell className="text-xs whitespace-nowrap">{fmtDate(l.created_at)}</TableCell>
                                                        <TableCell className="font-medium">{l.business_name}</TableCell>
                                                        <TableCell>{l.name}</TableCell>
                                                        <TableCell className="text-xs">{l.email}</TableCell>
                                                        <TableCell className="text-xs">{l.phone}</TableCell>
                                                        <TableCell>{l.industry}</TableCell>
                                                        <TableCell>{l.target_location}</TableCell>
                                                        <TableCell>{l.budget}</TableCell>
                                                        <TableCell className="max-w-xs truncate text-xs text-slate-600">{l.additional_notes || "—"}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="poster-apps" className="mt-4">
                        <Card>
                            <CardHeader><CardTitle className="font-display">Poster Applications</CardTitle></CardHeader>
                            <CardContent>
                                {posters.length === 0 ? (
                                    <div className="text-sm text-slate-500 py-8 text-center">{loading ? "Loading..." : "No applications yet."}</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Date</TableHead>
                                                    <TableHead>Name</TableHead>
                                                    <TableHead>Email</TableHead>
                                                    <TableHead>Phone</TableHead>
                                                    <TableHead>City</TableHead>
                                                    <TableHead>Avg Views</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {posters.map((p) => (
                                                    <TableRow key={p.id}>
                                                        <TableCell className="text-xs whitespace-nowrap">{fmtDate(p.created_at)}</TableCell>
                                                        <TableCell className="font-medium">{p.name}</TableCell>
                                                        <TableCell className="text-xs">{p.email}</TableCell>
                                                        <TableCell className="text-xs">{p.phone}</TableCell>
                                                        <TableCell>{p.city}</TableCell>
                                                        <TableCell>{p.avg_status_views}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="messages" className="mt-4">
                        <Card>
                            <CardHeader><CardTitle className="font-display">Contact Messages</CardTitle></CardHeader>
                            <CardContent>
                                {messages.length === 0 ? (
                                    <div className="text-sm text-slate-500 py-8 text-center">{loading ? "Loading..." : "No messages yet."}</div>
                                ) : (
                                    <div className="space-y-3">
                                        {messages.map((m) => (
                                            <div key={m.id} className="border border-slate-200 rounded-lg p-4 bg-white">
                                                <div className="flex flex-wrap justify-between gap-2 mb-2">
                                                    <div className="font-medium text-[#0A192F]">{m.name} <span className="text-xs text-slate-500">· {m.email}</span></div>
                                                    <div className="text-xs text-slate-400">{fmtDate(m.created_at)}</div>
                                                </div>
                                                {m.subject && <div className="text-sm font-semibold text-slate-700 mb-1">{m.subject}</div>}
                                                <div className="text-sm text-slate-600 whitespace-pre-wrap">{m.message}</div>
                                                {m.phone && <div className="text-xs text-slate-500 mt-2">📞 {m.phone}</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
